import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const infoDropdownItems = [
  { label: 'Şahıs Şirketi Nasıl Kurulur?', href: '/sahis-sirketi-nasil-kurulur', icon: 'ri-question-line' },
  { label: 'Vergi Oranları 2026', href: '/sahis-sirketi-vergi-oranlari', icon: 'ri-percent-line' },
  { label: 'Limited mi Şahıs mı?', href: '/limited-mi-sahis-mi', icon: 'ri-scales-line' },
  { label: 'E-Fatura Kimler İçin Zorunlu?', href: '/e-fatura-kimler-icin-zorunlu', icon: 'ri-file-text-line' },
  { label: 'Vergi Levhası Nasıl Alınır?', href: '/vergi-levhasi-nasil-alinir', icon: 'ri-award-line' },
  { label: 'Bağ-Kur Primi Ne Kadar?', href: '/bagkur-primi-ne-kadar', icon: 'ri-shield-user-line' },
  { label: 'KDV Nedir? Nasıl Hesaplanır?', href: '/kdv-nedir-nasil-hesaplanir', icon: 'ri-calculator-line' },
  { label: 'Vergi Borcu Sorgulama', href: '/vergi-borcu-sorgulama', icon: 'ri-search-line' },
  { label: 'Şirket Kapatma Nasıl Yapılır?', href: '/sirket-kapatma-nasil-yapilir', icon: 'ri-close-circle-line' },
  { label: 'Muhasebeci Değiştirme', href: '/muhasebeci-degistirme', icon: 'ri-refresh-line' },
];

const serviceDropdownItems = [
  { label: 'Şahıs Şirketi Kuruluşu', href: '/sahis-sirketi-kurulusu', icon: 'ri-user-line' },
  { label: 'Limited Şirket Kuruluşu', href: '/limited-sirket-kurulusu', icon: 'ri-building-line' },
  { label: 'Şirket Türü Seçimi', href: '/sirket-turu-secimi', icon: 'ri-compass-line' },
  { label: 'Vergi Danışmanlığı', href: '/vergi-danismanligi', icon: 'ri-percent-line' },
  { label: 'Genel Muhasebe', href: '/genel-muhasebe', icon: 'ri-book-2-line' },
  { label: 'SGK ve Bordro', href: '/sgk-bordro', icon: 'ri-group-line' },
  { label: 'E-Fatura / E-Arşiv', href: '/e-fatura-e-arsiv', icon: 'ri-file-text-line' },
  { label: 'Beyanname Hizmetleri', href: '/beyanname-hizmetleri', icon: 'ri-calendar-check-line' },
  { label: 'Mali Analiz ve Raporlama', href: '/mali-analiz', icon: 'ri-line-chart-line' },
  { label: 'Teşvik Danışmanlığı', href: '/tesvik-danismanligi', icon: 'ri-government-line' },
];

const otherNavLinks = [
  { label: 'Araçlar', href: '#araclar' },
  { label: 'Süreç', href: '#surec' },
  { label: 'Hakkımızda', href: '#hakkimizda' },
  { label: 'İletişim', href: '#iletisim' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [infoDropdownOpen, setInfoDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const infoDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (infoDropdownRef.current && !infoDropdownRef.current.contains(e.target as Node)) {
        setInfoDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    setMobileServicesOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const textColor = 'text-white/90';
  const hoverColor = 'hover:text-[#3B82F6]';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0A2540]/50 backdrop-blur-md border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="flex items-center cursor-pointer"
        >
          <img
            src="https://storage.readdy-site.link/project_files/20f355b3-57c6-42af-8f1b-e19ef75cee72/a013bf3f-953f-4147-a966-2d3225ff6ac1_zgr-akmak-logo-3.png?v=001ea3c7f21fa91e8ebb91c8becb8de9"
            alt="Özgür Çakmak SMMM Logo"
            className="h-12 w-auto object-contain"
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-7">
          {/* Hizmetler Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1 text-sm font-medium transition-colors ${hoverColor} cursor-pointer whitespace-nowrap ${textColor}`}
            >
              Hizmetler
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </div>
            </button>

            {/* Dropdown Panel */}
            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-lg">
                <div className="p-2">
                  {serviceDropdownItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F5F7FA] transition-colors cursor-pointer group"
                    >
                      <div className="w-7 h-7 flex items-center justify-center bg-[#0A2540]/5 rounded-lg flex-shrink-0 group-hover:bg-[#0A2540]/10 transition-colors">
                        <i className={`${item.icon} text-[#0A2540] text-sm`}></i>
                      </div>
                      <span className="text-[#0A2540] text-sm font-medium">{item.label}</span>
                      <div className="w-4 h-4 flex items-center justify-center ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                        <i className="ri-arrow-right-s-line text-[#0A2540] text-sm"></i>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="border-t border-gray-100 px-4 py-3">
                  <a
                    href="#hizmetler"
                    onClick={(e) => { e.preventDefault(); setDropdownOpen(false); handleNavClick('#hizmetler'); }}
                    className="flex items-center justify-center gap-1.5 text-xs text-[#0A2540]/50 hover:text-[#0A2540] transition-colors cursor-pointer"
                  >
                    <span>Tüm hizmetleri gör</span>
                    <i className="ri-arrow-right-line text-xs"></i>
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Bilgi Merkezi Dropdown */}
          <div className="relative" ref={infoDropdownRef}>
            <button
              onClick={() => { setInfoDropdownOpen(!infoDropdownOpen); setDropdownOpen(false); }}
              className={`flex items-center gap-1 text-sm font-medium transition-colors ${hoverColor} cursor-pointer whitespace-nowrap ${textColor}`}
            >
              Bilgi Merkezi
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${infoDropdownOpen ? 'rotate-180' : ''}`}></i>
              </div>
            </button>
            {infoDropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-lg">
                <div className="p-2">
                  {infoDropdownItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setInfoDropdownOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F5F7FA] transition-colors cursor-pointer group"
                    >
                      <div className="w-7 h-7 flex items-center justify-center bg-[#0A2540]/5 rounded-lg flex-shrink-0 group-hover:bg-[#0A2540]/10 transition-colors">
                        <i className={`${item.icon} text-[#0A2540] text-sm`}></i>
                      </div>
                      <span className="text-[#0A2540] text-sm font-medium">{item.label}</span>
                      <div className="w-4 h-4 flex items-center justify-center ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                        <i className="ri-arrow-right-s-line text-[#0A2540] text-sm"></i>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Other Links */}
          {otherNavLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
              className={`text-sm font-medium transition-colors ${hoverColor} cursor-pointer whitespace-nowrap ${textColor}`}
            >
              {link.label}
            </a>
          ))}
          <button
            onClick={() => navigate('/blog')}
            className={`text-sm font-medium transition-colors ${hoverColor} cursor-pointer whitespace-nowrap ${textColor}`}
          >
            Blog
          </button>
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="#iletisim"
            onClick={(e) => { e.preventDefault(); handleNavClick('#iletisim'); }}
            className="px-5 py-2 bg-[#0A2540] text-white text-sm font-medium rounded-lg hover:-translate-y-0.5 transition-all whitespace-nowrap cursor-pointer"
          >
            İletişime Geç
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer text-white"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menü"
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 flex flex-col gap-1">
          {/* Mobile Hizmetler Accordion */}
          <div>
            <button
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              className="w-full flex items-center justify-between text-[#0A2540] text-sm font-medium py-2.5 border-b border-gray-50 cursor-pointer"
            >
              <span>Hizmetler</span>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
              </div>
            </button>
            {mobileServicesOpen && (
              <div className="mt-1 mb-2 pl-3 flex flex-col gap-0.5">
                {serviceDropdownItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 py-2 px-2 rounded-lg hover:bg-[#F5F7FA] transition-colors cursor-pointer"
                  >
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-[#0A2540]/50 text-sm`}></i>
                    </div>
                    <span className="text-[#0A2540]/80 text-sm">{item.label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Bilgi Merkezi Accordion */}
          <div>
            <button
              onClick={() => setMobileInfoOpen(!mobileInfoOpen)}
              className="w-full flex items-center justify-between text-[#0A2540] text-sm font-medium py-2.5 border-b border-gray-50 cursor-pointer"
            >
              <span>Bilgi Merkezi</span>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileInfoOpen ? 'rotate-180' : ''}`}></i>
              </div>
            </button>
            {mobileInfoOpen && (
              <div className="mt-1 mb-2 pl-3 flex flex-col gap-0.5">
                {infoDropdownItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 py-2 px-2 rounded-lg hover:bg-[#F5F7FA] transition-colors cursor-pointer"
                  >
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-[#0A2540]/50 text-sm`}></i>
                    </div>
                    <span className="text-[#0A2540]/80 text-sm">{item.label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {otherNavLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
              className="text-[#0A2540] text-sm font-medium py-2.5 border-b border-gray-50 cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <button
            onClick={() => { setMenuOpen(false); navigate('/blog'); }}
            className="text-[#0A2540] text-sm font-medium py-2.5 border-b border-gray-50 cursor-pointer text-left w-full"
          >
            Blog
          </button>

          <a
            href="#iletisim"
            onClick={(e) => { e.preventDefault(); handleNavClick('#iletisim'); }}
            className="mt-2 px-5 py-2.5 bg-[#0A2540] text-white text-sm font-medium rounded-lg text-center cursor-pointer whitespace-nowrap"
          >
            İletişime Geç
          </a>
        </div>
      )}
    </header>
  );
}
